/**
 * Seletor de centro de custo e editor de rateio comuns (ADR 0092), para qualquer formulário. Os
 * centros vêm de `GET /core/cost-centers/eligibility`; a conferência e o cálculo são os de
 * `@loot/shared` (os mesmos do servidor, que confere de novo ao gravar).
 */
import {
  computeCostSplit,
  costCenterLabel,
  costSplitShareSum,
  evenPercents,
  remainderForLast,
  type CostSplitInput,
  type CostSplitIssue,
  type CostSplitMethod,
} from '@loot/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api.ts';
import {
  cascadeFor,
  cascadeOptions,
  reconcileCascade,
  type CascadeRules,
  type CascadeSelection,
  type EligibleCostCenter,
} from './cost-center-picker.ts';
import { Badge, Button, Field, Input, Select } from './ui.tsx';

export function useCostCenterEligibility() {
  return useQuery({
    queryKey: ['cost-centers', 'eligibility'],
    queryFn: () => api.get<EligibleCostCenter[]>('/core/cost-centers/eligibility'),
    staleTime: 5 * 60_000,
  });
}

export interface CostCenterPickerProps extends CascadeRules {
  value: string;
  onChange: (costCenterId: string, center: EligibleCostCenter | null) => void;
  /** Empresa fixa da operação; sem ela, o seletor mostra a escolha da empresa. */
  legalEntityId?: string | null;
  label?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
}

/**
 * Centro único com filtros em cascata (empresa → departamento → Master → Gerencial → centro).
 * Os filtros de cima só ajudam a achar; o que vale é o centro escolhido.
 */
export function CostCenterPicker(props: CostCenterPickerProps) {
  const { t } = useTranslation();
  const query = useCostCenterEligibility();
  const nodes = query.data ?? [];
  const rules: CascadeRules = {
    levels: props.levels,
    allowUnleveled: props.allowUnleveled,
    keepIds: props.keepIds,
    onDate: props.onDate,
  };
  const [own, setOwn] = useState<CascadeSelection | null>(null);
  const sel = {
    ...(own ?? cascadeFor(nodes, props.value, props.legalEntityId)),
    ...(props.legalEntityId ? { legalEntityId: props.legalEntityId } : {}),
  };
  const opts = cascadeOptions(nodes, sel, rules);
  const change = (patch: Partial<CascadeSelection>) => {
    const next = reconcileCascade(nodes, { ...sel, ...patch }, props.value, rules);
    setOwn(next.sel);
    if (next.value !== props.value) props.onChange(next.value, null);
  };
  const ctl = (label: string, node: ReactNode) => (
    <Field label={label} className="min-w-40 flex-1">
      {node}
    </Field>
  );
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        {!props.legalEntityId &&
          ctl(
            t('costCenterPicker.company'),
            <Select
              value={sel.legalEntityId}
              disabled={props.disabled}
              onChange={(e) =>
                change({
                  legalEntityId: e.target.value,
                  departmentId: '',
                  masterId: '',
                  managerialId: '',
                })
              }
            >
              <option value="">{t('common.allCompanies')}</option>
              {opts.companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code}
                </option>
              ))}
            </Select>,
          )}
        {ctl(
          t('costCenterPicker.department'),
          <Select
            value={sel.departmentId}
            disabled={props.disabled}
            onChange={(e) =>
              change({ departmentId: e.target.value, masterId: '', managerialId: '' })
            }
          >
            <option value="">{t('costCenterPicker.anyDepartment')}</option>
            {opts.departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.label}
              </option>
            ))}
          </Select>,
        )}
        {ctl(
          t('costCenterPicker.master'),
          <Select
            value={sel.masterId}
            disabled={props.disabled}
            onChange={(e) => change({ masterId: e.target.value, managerialId: '' })}
          >
            <option value="">{t('costCenterPicker.anyMaster')}</option>
            {opts.masters.map((m) => (
              <option key={m.id} value={m.id}>
                {costCenterLabel(m.code, m.name)}
              </option>
            ))}
          </Select>,
        )}
        {opts.managerials.length > 0 &&
          ctl(
            t('costCenterPicker.managerial'),
            <Select
              value={sel.managerialId}
              disabled={props.disabled}
              onChange={(e) => change({ managerialId: e.target.value })}
            >
              <option value="">{t('costCenterPicker.anyManagerial')}</option>
              {opts.managerials.map((m) => (
                <option key={m.id} value={m.id}>
                  {costCenterLabel(m.code, m.name)}
                </option>
              ))}
            </Select>,
          )}
      </div>
      <Field
        label={props.label ?? t('costCenterPicker.center')}
        required={props.required}
        error={props.error}
      >
        <Select
          value={props.value}
          disabled={props.disabled || query.isPending}
          onChange={(e) =>
            props.onChange(e.target.value, nodes.find((n) => n.id === e.target.value) ?? null)
          }
        >
          <option value="">
            {query.isPending
              ? t('common.loading')
              : opts.centers.length
                ? t('common.choose')
                : t('costCenterPicker.noneEligible')}
          </option>
          {opts.centers.map((c) => (
            <option key={c.id} value={c.id}>
              {costCenterLabel(c.code, c.name)}
              {props.legalEntityId ? '' : ` (${c.legalEntityCode})`}
            </option>
          ))}
        </Select>
      </Field>
    </div>
  );
}

export interface CostSplitEditorProps extends CascadeRules {
  value: CostSplitInput;
  onChange: (value: CostSplitInput) => void;
  /** Empresa da operação (contratante). */
  legalEntityId: string;
  /** O processo aceita parcelas em outras empresas do grupo. */
  allowCrossCompany: boolean;
  /** Valor total, para a prévia por centro (texto decimal). */
  total?: string | null;
  currency?: string | null;
  /** Casas da moeda (BRL e USD: 2). */
  minorUnits?: number;
  /** Métodos que o processo aceita (padrão: os três). */
  methods?: readonly CostSplitMethod[];
  /** Problemas devolvidos pelo servidor ao gravar (somam-se aos da prévia). */
  issues?: readonly CostSplitIssue[];
  disabled?: boolean;
  /** A área pagadora vem de fora (ex.: o Centro de Custo Master da compra): não pergunta aqui. */
  hidePayer?: boolean;
}

const fmt = (v: string, locale: string, digits = 2) =>
  new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: 4 }).format(
    v as unknown as number,
  );

/**
 * Rateio entre centros de custo no padrão do "Para quem" do FP&A: método, adicionar centro,
 * dividir igualmente, completar o último, soma, prévia por centro e área pagadora confirmada.
 * Cada linha tem o seu próprio filtro: a primeira não restringe as demais.
 */
export function CostSplitEditor(props: CostSplitEditorProps) {
  const { t, i18n } = useTranslation();
  const query = useCostCenterEligibility();
  const nodes = useMemo(() => query.data ?? [], [query.data]);
  const v = props.value;
  const methods = props.methods ?? (['percent', 'quantity', 'amount'] as const);
  const filled = v.lines.filter((l) => l.costCenterId);
  // Valor fixo sem total conhecido (compra sem preço): a soma dos valores é o total.
  const total =
    v.method === 'amount' && props.total == null ? costSplitShareSum(filled) : props.total;
  const preview = computeCostSplit(
    { ...v, lines: filled.length ? filled : [{ costCenterId: '', share: '0' }] },
    nodes,
    {
      legalEntityId: props.legalEntityId,
      allowCrossCompany: props.allowCrossCompany,
      levels: props.levels,
      allowUnleveled: props.allowUnleveled,
      keepIds: props.keepIds,
      onDate: props.onDate,
      total,
      minorUnits: props.minorUnits,
    },
  );
  const lineOf = (id: string) =>
    id ? preview.lines.find((l) => l.costCenterId === id) : undefined;
  const issuesOf = (i: number) =>
    [...preview.issues, ...(props.issues ?? [])].filter((x) => {
      const f = filled.indexOf(v.lines[i]!);
      return f >= 0 && x.path.startsWith(`lines.${f}.`);
    });
  const global = [...(filled.length ? preview.issues : []), ...(props.issues ?? [])].filter(
    (x) => !x.path.startsWith('lines.'),
  );
  const set = (patch: Partial<CostSplitInput>) => props.onChange({ ...v, ...patch });
  const setLine = (i: number, patch: Partial<CostSplitInput['lines'][number]>) =>
    set({ lines: v.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const msg = (x: CostSplitIssue) =>
    t(`costSplit.issue.${x.code}`, { sum: x.sum ? fmt(x.sum, i18n.language) : '' });
  const shareSum = v.lines.reduce(
    (s, l) => (/^\d+(\.\d+)?$/.test(l.share) ? s + Number(l.share) : s),
    0,
  );
  const company = (id: string) => nodes.find((n) => n.legalEntityId === id)?.legalEntityCode ?? '';
  const suggested = preview.suggestedPayerCostCenterId;

  return (
    <div className="flex flex-col gap-3">
      {methods.length > 1 && (
        <Field label={t('costSplit.method')} hint={t(`costSplit.methodHint.${v.method}`)}>
          <div role="radiogroup" className="flex flex-wrap gap-2">
            {methods.map((m) => (
              <Button
                key={m}
                role="radio"
                aria-checked={v.method === m}
                variant={v.method === m ? undefined : 'secondary'}
                disabled={props.disabled}
                onClick={() => set({ method: m })}
              >
                {t(`costSplit.methods.${m}`)}
              </Button>
            ))}
          </div>
        </Field>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="secondary"
          disabled={props.disabled}
          onClick={() => set({ lines: [...v.lines, { costCenterId: '', share: '' }] })}
        >
          {t('costSplit.addCenter')}
        </Button>
        {v.method === 'percent' && (
          <Button
            variant="secondary"
            disabled={props.disabled || v.lines.length < 2}
            onClick={() => {
              const p = evenPercents(
                v.lines.map((l, i) => lineOf(l.costCenterId)?.costCenterCode ?? String(i)),
              );
              set({ lines: v.lines.map((l, i) => ({ ...l, share: p[i]!.replace(/\.?0+$/, '') })) });
            }}
          >
            {t('costSplit.splitEqually')}
          </Button>
        )}
        {v.method !== 'quantity' && (v.method === 'percent' || props.total != null) && (
          <Button
            variant="secondary"
            disabled={props.disabled || v.lines.length < 1}
            onClick={() => {
              const rest = remainderForLast(
                v.method,
                v.lines.map((l) => l.share),
                props.total,
              );
              if (rest) setLine(v.lines.length - 1, { share: rest.replace(/\.?0+$/, '') });
            }}
          >
            {t(v.method === 'percent' ? 'costSplit.completeLast' : 'costSplit.completeLastAmount')}
          </Button>
        )}
        <Badge tone={preview.ok ? 'ok' : 'accent'}>
          {t(`costSplit.sum.${v.method}`, { sum: fmt(String(shareSum), i18n.language, 0) })}
        </Badge>
      </div>
      {v.lines.map((l, i) => {
        const line = lineOf(l.costCenterId);
        const other = line && line.legalEntityId !== props.legalEntityId;
        const issues = issuesOf(i);
        return (
          <div key={i} className="flex flex-col gap-2 rounded-lg border border-rule p-3">
            <CostCenterPicker
              value={l.costCenterId}
              onChange={(id, c) =>
                setLine(i, { costCenterId: id, legalEntityId: c?.legalEntityId ?? null })
              }
              legalEntityId={props.allowCrossCompany ? null : props.legalEntityId}
              levels={props.levels}
              allowUnleveled={props.allowUnleveled}
              keepIds={props.keepIds}
              onDate={props.onDate}
              required
              disabled={props.disabled}
              error={
                issues
                  .filter(
                    (x) => x.path.endsWith('.costCenterId') || x.path.endsWith('.legalEntityId'),
                  )
                  .map(msg)
                  .join(' ') || undefined
              }
            />
            <div className="flex flex-wrap items-end gap-2">
              <Field
                label={t(`costSplit.share.${v.method}`)}
                required
                className="w-40"
                error={
                  issues
                    .filter((x) => x.path.endsWith('.share'))
                    .map(msg)
                    .join(' ') || undefined
                }
              >
                <Input
                  inputMode="decimal"
                  value={l.share}
                  disabled={props.disabled}
                  onChange={(e) => setLine(i, { share: e.target.value.replace(',', '.') })}
                />
              </Field>
              {line?.percent && v.method !== 'percent' && (
                <Badge tone="info">{fmt(line.percent, i18n.language)}%</Badge>
              )}
              {line?.amount && (
                <Badge tone="neutral">
                  {props.currency
                    ? new Intl.NumberFormat(i18n.language, {
                        style: 'currency',
                        currency: props.currency,
                      }).format(line.amount as unknown as number)
                    : fmt(line.amount, i18n.language)}
                </Badge>
              )}
              {other && (
                <Badge tone="accent">
                  {t('costSplit.beneficiary', { company: company(line.legalEntityId) })}
                </Badge>
              )}
              {!props.hidePayer && (
                <label className="flex items-center gap-1 text-sm">
                  <input
                    type="radio"
                    name="cost-split-payer"
                    checked={!!l.costCenterId && v.payerCostCenterId === l.costCenterId}
                    disabled={props.disabled || !l.costCenterId}
                    onChange={() => set({ payerCostCenterId: l.costCenterId })}
                  />
                  {t('costSplit.payer')}
                </label>
              )}
              <Button
                variant="secondary"
                disabled={props.disabled || v.lines.length < 2}
                onClick={() =>
                  set({
                    lines: v.lines.filter((_, j) => j !== i),
                    payerCostCenterId:
                      v.payerCostCenterId === l.costCenterId ? null : v.payerCostCenterId,
                  })
                }
              >
                {t('common.remove')}
              </Button>
            </div>
          </div>
        );
      })}
      {!props.hidePayer && !v.payerCostCenterId && suggested && filled.length > 0 && (
        <p role="status" className="flex flex-wrap items-center gap-2 text-sm text-muted">
          {t('costSplit.payerSuggestion', {
            center: lineOf(suggested)?.costCenterCode ?? '',
          })}
          <Button
            variant="secondary"
            disabled={props.disabled}
            onClick={() => set({ payerCostCenterId: suggested })}
          >
            {t('costSplit.confirmSuggestion')}
          </Button>
        </p>
      )}
      {preview.crossCompany && (
        <p className="text-xs text-muted">{t('costSplit.crossCompanyNote')}</p>
      )}
      {global.length > 0 && (
        <ul role="alert" className="text-sm text-danger">
          {global.map((x) => (
            <li key={`${x.path}-${x.code}`}>{msg(x)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
